import { Box, Divider, Group, Input, Select, Tooltip } from '@byecode/ui'
import type { FlowNode, NodeTypes, SearchApiActionConfig } from '@lighthouse/shared'
import {
    getAllCanBeUpstreamNodes,
    getInputInnerTypeMatch,
    getUpstreamNodesOptions,
    NodeFieldPreview,
    OtherPlatformNodeType,
    SearchApiResultType,
    useAtomData
} from '@lighthouse/shared'
import React, { useMemo } from 'react'

import { otherPlatformListAtom } from '@/atoms/application/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import OutputVariable from '@/containers/FlowDetail/components/OutputVariable'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useGetUserRecordOptions } from '@/hooks/usePage'

import { ActionAuthIntegrateCard } from '../../Common/ActionAuthIntegrateCard'
import { UnitNoAuth } from '../../Common/UnitNoAuth'
import { SEARCH_RESULT_TYPES } from '../../constant'
import * as SC from './styles'

type Props = {
    nodeType?: NodeTypes
    allParentNodes: FlowNode[]
    value: SearchApiActionConfig
    onChange: (value: SearchApiActionConfig) => void
}

const options = [
    { label: '文本', value: SearchApiResultType.TEXT },
    { label: '链接', value: SearchApiResultType.LINK }
]

export const BingSearchActionConfigure: React.FC<Props> = ({ allParentNodes, value, onChange }) => {
    const otherPlatformList = useAtomData(otherPlatformListAtom)
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const { type } = useFlowContainerContext()
    const isInFlow = !!type
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const upstreamOptions = useMemo(
        () =>
            isInFlow
                ? getUpstreamNodesOptions({
                      workflowList,
                      upstreamNodes: getAllCanBeUpstreamNodes(allParentNodes || []),
                      allUpstreamNodes: allParentNodes,
                      dataSourceList
                  })
                : undefined,
        [allParentNodes, dataSourceList, isInFlow, workflowList]
    )

    // const pageId = useAtomData(
    //     lastPageOfStackAtom,
    //     useCallback(s => s?.pageId || '', [])
    // )

    const userOption = useGetUserRecordOptions()

    const searchApiAccount = useMemo(() => otherPlatformList?.find(item => item.type.includes('SEARCH_API')), [otherPlatformList])

    if (!searchApiAccount) {
        return <UnitNoAuth type="SEARCH_API" />
    }

    return (
        <SC.Container>
            <ActionAuthIntegrateCard type="SEARCH_API" />
            <Divider />

            <Group label="输入变量">
                <Box px={8}>
                    <SC.Item>
                        <SC.Text>
                            询问
                            <SC.Text color="red" style={{ paddingLeft: 4 }}>
                                *
                            </SC.Text>
                        </SC.Text>
                        <SC.FormItemWrapper>
                            <NodeFieldPreview
                                innerTypes={getInputInnerTypeMatch('TEXT')}
                                placeholder="在这里写你要搜索的内容"
                                autoHeight
                                upstreamOptions={upstreamOptions}
                                value={value.query}
                                onBlur={query => onChange({ ...value, query })}
                                userOption={userOption}
                                dataSourceList={dataSourceList}
                            />
                        </SC.FormItemWrapper>
                    </SC.Item>
                    <SC.Item>
                        <SC.Text>每页结果数</SC.Text>
                        <SC.FormItemWrapper>
                            <Input
                                placeholder="请输入正整数，例如“10”"
                                type="number"
                                value={value.numberOfResult}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    const v = e.target.value
                                    const isValidNumber = /^\d+$/.test(v)
                                    const isAboveMin = Number(v) >= 1
                                    if (isValidNumber && isAboveMin) {
                                        onChange({ ...value, numberOfResult: Number(v) })
                                    }
                                }}
                            />
                        </SC.FormItemWrapper>
                    </SC.Item>

                    <SC.Item>
                        <SC.Text>
                            结果类型
                            <Tooltip
                                title={
                                    <SC.TooltipContent>
                                        用于选择结果类型，使用文
                                        <br />
                                        本还是链接进行展示
                                    </SC.TooltipContent>
                                }
                            >
                                <SC.Icon type="Question" size={14} />
                            </Tooltip>
                        </SC.Text>
                        <SC.FormItemWrapper>
                            <Select
                                options={SEARCH_RESULT_TYPES}
                                value={value.resultType}
                                onChange={resultType =>
                                    onChange({ ...value, resultType: resultType as SearchApiActionConfig['resultType'] })
                                }
                            />
                        </SC.FormItemWrapper>
                    </SC.Item>
                </Box>
            </Group>

            <OutputVariable nodeType={OtherPlatformNodeType.BING_SEARCH_ACTION} />
        </SC.Container>
    )
}
